import React, {useEffect} from 'react';
import Form from 'react-bootstrap/Form';
import Button from '@/components/ui/Button';
import {Formik} from 'formik';
import Col from 'react-bootstrap/Col';
import axios from 'axios';
import CellLoader from '@/components/loading/CellLoader';
import useData from '@/utils/useData';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {usePlatformPermissions} from '@/utils/platformPermissions';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import {UserPreferences} from './stores/user_preferences';
import {Tooltip} from '@/components/tooltips';
import {Row} from 'react-bootstrap';
import i18n from 'i18n';
import {Toggle} from '@/components/ui';
import {useStoreActions} from 'store';
import {useAlert} from '@/components/alerts/AlertContext';
import {extractErrorMessage} from '@/utils/errorHandling';

const PreferencesView = React.memo(() => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const platformPermissions = usePlatformPermissions(userData);
    const [preferences, loadingPreferences, updatePreferences] = useData<UserPreferences>(window.URLS.userPreferencesResource, {
        useLocalStore: true,
        raiseErrors: false,
    });
    const setUserDataReload = useStoreActions((actions) => actions.userData.setReload);
    const {showSuccess, showError} = useAlert();

    const getPreferenceDisplayName = (key: keyof UserPreferences): string => {
        const displayNames = {
            sendReports: i18n.t('jt_receive_reports.text'),
            helpInformation: i18n.t('jt_help_information.text'),
            dataAnonymized: i18n.t('jt_data_anonymisation.text'),
            menuMinified: 'Menu'
        };
        return displayNames[key as keyof typeof displayNames] || key;
    };

    const onSubmit = async (values: UserPreferences) => {
        try {
            // Update local state immediately for responsive UI
            updatePreferences(values);

            // Save to server
            const url = window.URLS.userPreferencesResource;
            await axios.post(url, values);

            // Notify other UI parts (e.g. header) that preferences have changed
            // so they can update their local view of the toggles.
            try {
                window.dispatchEvent(new CustomEvent('userPreferencesUpdated', {detail: values}));
            } catch (e) {
                // In older browsers CustomEvent might not be available; fail silently.
                console.warn('userPreferencesUpdated event dispatch failed', e);
            }

            // Check what changed and show appropriate messages
            const changedSettings: string[] = [];
            if (preferences) {
                Object.keys(values).forEach(key => {
                    const prefKey = key as keyof UserPreferences;
                    if (preferences[prefKey] !== values[prefKey]) {
                        const displayName = getPreferenceDisplayName(prefKey);
                        const status = values[prefKey] ? 'enabled' : 'disabled';
                        changedSettings.push(`${displayName} ${status}`);
                    }
                });
            }

            // Show success message with what changed
            if (changedSettings.length > 0) {
                showSuccess(`Settings updated: ${changedSettings.join(', ')}`);
            } else {
                showSuccess('Settings saved successfully');
            }

            // Trigger userData reload for settings that affect UI rendering
            const needsReload = preferences?.dataAnonymized !== values.dataAnonymized ||
                preferences?.helpInformation !== values.helpInformation;

            if (needsReload) {
                setUserDataReload(true);
            }

        } catch (error) {
            console.error('Error updating preferences:', error);
            const errorMessage = extractErrorMessage(error, 'Failed to update settings. Please try again.');
            showError(errorMessage);
            // Revert local state on error
            updatePreferences(preferences);
        }
    };

    // Keep Settings view in sync when preferences are changed elsewhere (e.g. from Header)
    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as any).detail as UserPreferences | undefined;
            if (detail) {
                updatePreferences(detail);
            }
        };

        window.addEventListener('userPreferencesUpdated', handler as EventListener);
        return () => {
            window.removeEventListener('userPreferencesUpdated', handler as EventListener);
        };
    }, [updatePreferences]);

    if (loadingUserData || loadingPreferences) {
        return (
            <CellLoader />
        );
    }

    return (
        <Formik
            initialValues={preferences || {
                menuMinified: false,
                sendReports: true,
                helpInformation: true,
                dataAnonymized: false,
                autoScrollEventView: false,
                autoScrollSearchView: false,
                autoScrollTextSamples: false,
            }}
            enableReinitialize={true}
            onSubmit={onSubmit}
        >
            {(formik) => (
                <Form noValidate onSubmit={formik.handleSubmit}>
                    <Row>
                        <Col xs={12}>
                            <HelpTextBlock helpText={i18n.t('ht_the_user.text')} />
                        </Col>
                    </Row>


                    <Row>
                        <Col xs={6}>
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex no-block align-items-center">
                                        <div>
                                            <h5>{i18n.t('jt_receive_reports.text')}</h5>
                                            <h6 className="mb-0">{i18n.t('jt_enable_receiving.text')}</h6>
                                        </div>
                                        <div className="ml-auto">
                                            <Toggle
                                                checked={formik.values.sendReports}
                                                onChange={(checked) => formik.setFieldValue('sendReports', checked)}
                                                id="sendReportsActive"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={6}>
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex no-block align-items-center">
                                        <div>
                                            <h5>{i18n.t('jt_help_information.text')}</h5>
                                            <h6 className="mb-0">
                                                {i18n.t('jt_enable_help.text')}
                                            </h6>
                                        </div>
                                        <div className="ml-auto">
                                            <Toggle
                                                checked={formik.values.helpInformation}
                                                onChange={(checked) => formik.setFieldValue('helpInformation', checked)}
                                                id="helpInformationActive"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={6}>
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex no-block align-items-center">
                                        <div>
                                            <h5>{i18n.t('jt_data_anonymisation.text')}</h5>
                                            <h6 className="mb-0">
                                                {i18n.t('jt_enable_data.text')}
                                            </h6>
                                        </div>
                                        <div className="ml-auto">
                                            {platformPermissions.canAccessUnanonymizedIndex
                                                ? <Toggle
                                                    checked={formik.values.dataAnonymized}
                                                    onChange={(checked) => formik.setFieldValue('dataAnonymized', checked)}
                                                    id="dataAnonymizedActive"
                                                />
                                                : <Tooltip
                                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                    placement='bottom'
                                                >
                                                    <div>
                                                        <Toggle
                                                            checked={true}
                                                            onChange={() => { }}
                                                            id="dataAnonymizedActive"
                                                            disabled={true}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={6}>
                            <Form.Group className="mb-3" controlId="action-save" >
                                <Button
                                    variant="info"
                                    type="submit"
                                    data-cy="preferences-info-btn">
                                    {i18n.t('jt_save_changes.text')}
                                </Button>
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            )
            }
        </Formik >
    );
});


export default PreferencesView;
